"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useExperienciaProduto } from "./ExperienciaProvider";
import {
  ehAreaGerencialFollowPro,
  itemSubmenuFollowZapAtivo,
  moduloFollowZapAtivo,
  modulosFollowZapParaContexto,
  podeMostrarConfiguracoesMenuPerfil,
  submenuFollowZapDoPathname,
} from "@/lib/followzap-navigation";
import { MenuPerfil } from "./MenuPerfil";

export function FollowZapExperimentalNavigation({ ativo }: { ativo: boolean }) {
  const pathname = usePathname();
  const { dados, experiencia, carregado, experienciaConfirmada } = useExperienciaProduto();
  const areaGerencialAnalise = ehAreaGerencialFollowPro(pathname, dados?.produto, dados?.papel);
  const modulos = ativo && carregado && experienciaConfirmada
    ? modulosFollowZapParaContexto({
        produto: experiencia.produto,
        papel: dados?.papel ?? null,
        recursos: experiencia.recursos,
        modoAvancado: true,
        mostrarFollowUp: dados?.papel === "USUARIO",
        areaGerencialAnalise,
        modulosFollowProUsuario: dados?.modulosFollowProUsuario,
      }) ?? []
    : [];
  const configuracoesNoMenu = podeMostrarConfiguracoesMenuPerfil(dados?.produto, dados?.papel, experiencia.recursos.configuracoes);
  const principais = configuracoesNoMenu ? modulos.filter((modulo) => modulo.id !== "configuracoes") : modulos;
  const moduloAtivo = moduloFollowZapAtivo(pathname, principais);
  const submenu = submenuFollowZapDoPathname(pathname, "desktop", principais);

  const classeItem = (selecionado: boolean) =>
    `inline-flex h-9 shrink-0 items-center whitespace-nowrap border-b-2 px-3 text-[13px] font-semibold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]/40 ${
      selecionado
        ? "border-[var(--fz-tabs-active-text)] text-[var(--fz-tabs-active-text)]"
        : "border-transparent text-[var(--fz-header-text-muted)] hover:text-[var(--fz-header-text)]"
    }`;

  return (
    <div
      aria-hidden={ativo ? undefined : true}
      className={ativo ? "relative z-50 shrink-0 px-4 pt-3" : "hidden"}
      data-followzap-experimental-navigation={ativo ? "true" : undefined}
    >
      <div
        className="mx-auto flex min-h-12 w-full max-w-full items-center gap-3 px-1 py-1"
        data-followzap-experimental-main-navigation="true"
      >
        <Link
          href={areaGerencialAnalise ? "/analise" : "/"}
          prefetch
          className="flex min-w-0 shrink-0 items-center gap-2.5 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]/40"
          data-followzap-experimental-brand="true"
        >
          <span data-user-desktop-brand="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-extrabold text-white">
            {areaGerencialAnalise ? "FP" : experienciaConfirmada ? experiencia.marca.sigla : null}
          </span>
          <strong className="block truncate text-[13px] text-[var(--fz-header-text)]" data-followzap-experimental-brand-title="true">
            {areaGerencialAnalise ? "FollowPro" : experienciaConfirmada ? experiencia.marca.nome : "FollowZap"}
          </strong>
        </Link>

        <nav
          aria-label={experiencia.produto === "FOLLOW_PRO" ? "Navegação principal do FollowPro" : "Navegação principal do FollowZap"}
          className="flex min-w-0 flex-1 items-center justify-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-followzap-experimental-main-links="true"
        >
          {principais.map((modulo) => {
            const selecionado = modulo.id === moduloAtivo?.id;
            return (
              <Link
                key={modulo.id}
                href={modulo.href}
                prefetch
                aria-current={selecionado ? "page" : undefined}
                className={classeItem(selecionado)}
                data-followzap-navigation-item={selecionado ? "active" : "inactive"}
              >
                {modulo.label}
              </Link>
            );
          })}
        </nav>
        <div className="ml-auto shrink-0">
          {ativo && dados ? <MenuPerfil /> : null}
        </div>
      </div>

      <div className="contents" data-followzap-experimental-context-slot="true">
        {submenu ? (
          <nav
            aria-label="Navegação contextual"
            className="mx-auto mt-1 flex min-h-9 w-fit max-w-full items-center gap-1 overflow-x-auto px-1 py-0.5"
            data-followzap-experimental-submenu="true"
          >
            {submenu.itens.map((item) => {
              const selecionado = itemSubmenuFollowZapAtivo(pathname, item);
              return (
                <Link
                  key={item.id}
                  href={item.href}
                  prefetch
                  aria-current={selecionado ? "page" : undefined}
                  className={`inline-flex h-8 shrink-0 items-center border-b-2 px-3 text-xs font-semibold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]/40 ${
                    selecionado
                      ? "border-[var(--fz-tabs-active-text)] text-[var(--fz-tabs-active-text)]"
                      : "border-transparent text-[var(--fz-header-text-muted)] hover:text-[var(--fz-header-text)]"
                  }`}
                  data-followzap-submenu-item={selecionado ? "active" : "inactive"}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        ) : null}
      </div>
    </div>
  );
}
